import { useMemo, type CSSProperties } from 'react';

import type { SanityImageCrop, SanityImageHotspot } from '@/sanity.types';

export type SanityHotspotSource =
  | {
      crop?: SanityImageCrop | null;
      hotspot?: SanityImageHotspot | null;
    }
  | null
  | undefined;

const clamp = (value: number) => Math.min(1, Math.max(0, value));

const formatPercent = (value: number) => `${Math.round(value * 1000) / 10}%`; // one decimal precision

export const getSanityHotspotObjectPosition = (image?: SanityHotspotSource): string | undefined => {
  if (!image || !image.hotspot) {
    return undefined;
  }

  const hotspot = image.hotspot;
  const crop = image.crop;

  const cropLeft = crop?.left ?? 0;
  const cropRight = crop?.right ?? 0;
  const cropTop = crop?.top ?? 0;
  const cropBottom = crop?.bottom ?? 0;

  const width = 1 - cropLeft - cropRight;
  const height = 1 - cropTop - cropBottom;

  if (width <= 0 || height <= 0) {
    return undefined;
  }

  const normalizedX = clamp((hotspot.x - cropLeft) / width);
  const normalizedY = clamp((hotspot.y - cropTop) / height);

  return `${formatPercent(normalizedX)} ${formatPercent(normalizedY)}`;
};

interface UseSanityHotspotReturn {
  objectPosition?: string;
  className?: string;
  style?: CSSProperties;
}

export const useSanityHotspot = (image?: SanityHotspotSource): UseSanityHotspotReturn => {
  const objectPosition = useMemo(() => getSanityHotspotObjectPosition(image), [image]);

  const style = useMemo(() => {
    if (!objectPosition) {
      return undefined;
    }

    return {
      '--sanity-hotspot': objectPosition,
    } as CSSProperties;
  }, [objectPosition]);

  return {
    objectPosition,
    className: objectPosition ? 'object-(--sanity-hotspot)' : undefined,
    style,
  };
};
